import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Database,
  RefreshCw,
  Download,
  Sparkles,
  Clock,
  Play,
  Loader2,
  Server,
  Globe,
  Users,
  Upload,
  Shield
} from 'lucide-react';
import SyncLogsList from './data-management/SyncLogsList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  green: {
    pill: 'bg-emerald-500 text-white',
    ring: 'ring-emerald-200',
    dot: 'bg-white',
    label: 'Fresh',
    accent: 'text-emerald-600',
    border: 'border-emerald-200 hover:border-emerald-400',
  },
  yellow: {
    pill: 'bg-amber-500 text-white',
    ring: 'ring-amber-200',
    dot: 'bg-white',
    label: 'Stale',
    accent: 'text-amber-600',
    border: 'border-amber-200 hover:border-amber-400',
  },
  red: {
    pill: 'bg-rose-600 text-white',
    ring: 'ring-rose-200',
    dot: 'bg-white',
    label: 'Failed',
    accent: 'text-rose-600',
    border: 'border-rose-200 hover:border-rose-400',
  },
  unknown: {
    pill: 'bg-slate-400 text-white',
    ring: 'ring-slate-200',
    dot: 'bg-white',
    label: 'Unknown',
    accent: 'text-slate-500',
    border: 'border-slate-200 hover:border-slate-400',
  },
};

const formatAge = (hours) => {
  if (hours == null) return '—';
  if (hours < 1) return '<1h ago';
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

const formatCount = (num) => (num?.toLocaleString() || '0');

const StatusCard = ({ freshness, source, label, count, Icon, iconBg, iconColor, testId }) => {
  const f = freshness[source];
  const style = STATUS_STYLE[f?.status] || STATUS_STYLE.unknown;
  const tooltip = f?.last_successful_sync
    ? `Last successful sync: ${new Date(f.last_successful_sync).toLocaleString()}${f.last_error ? '\n\nLast error: ' + f.last_error : ''}`
    : 'No successful sync recorded yet';
  return (
    <div
      className={`relative rounded-xl border-2 ${style.border} bg-white p-4 transition-all shadow-sm hover:shadow-md`}
      title={tooltip}
      data-testid={`status-card-${testId}`}
    >
      <div
        className={`absolute -top-3 right-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${style.pill} shadow-md ring-4 ${style.ring}`}
        data-testid={`freshness-pill-${testId}`}
      >
        <span className={`w-2 h-2 rounded-full ${style.dot} animate-pulse`} />
        <span className="uppercase tracking-wide">{style.label}</span>
      </div>

      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>

      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1" data-testid={`${testId}-count`}>
        {formatCount(count)}
      </p>
      <div className="mt-2 flex items-center gap-1.5 text-xs">
        <Clock className={`w-3.5 h-3.5 ${style.accent}`} />
        <span className={`font-medium ${style.accent}`}>Updated {formatAge(f?.age_hours)}</span>
      </div>
    </div>
  );
};

export default function DataManagementPage({ user, onLogout }) {
  const isSuperAdmin = user?.role === 'super_admin';
  const [loading, setLoading] = useState(true);
  const [watchlistCounts, setWatchlistCounts] = useState(null);
  const [enrichmentStatus, setEnrichmentStatus] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncing, setSyncing] = useState({ ofac: false, pep: false, wikidata: false, moha: false, unscr: false });
  const [freshness, setFreshness] = useState({});
  const [mohaUploading, setMohaUploading] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const [countsRes, enrichmentRes, schedulerRes, logsRes, freshRes] = await Promise.all([
        fetch(`${API_URL}/api/watchlist/count`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/watchlist/enrichment-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/watchlist/scheduler-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/watchlist/sync-logs?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/watchlist/freshness`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (countsRes.ok) setWatchlistCounts(await countsRes.json());
      if (enrichmentRes.ok) setEnrichmentStatus(await enrichmentRes.json());
      if (schedulerRes.ok) setSchedulerStatus(await schedulerRes.json());
      if (logsRes.ok) setSyncLogs(await logsRes.json());
      if (freshRes.ok) {
        const fresh = await freshRes.json();
        const map = {};
        (fresh.sources || []).forEach(s => { map[s.source] = s; });
        setFreshness(map);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data management info');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };


  const triggerOFACSync = async () => {
    setSyncing(prev => ({ ...prev, ofac: true }));
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/watchlist/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('OFAC sync started in background');
        setTimeout(fetchData, 5000);
      } else {
        toast.error('Failed to start OFAC sync');
      }
    } catch (error) {
      toast.error('Error starting sync');
    } finally {
      setSyncing(prev => ({ ...prev, ofac: false }));
    }
  };

  const triggerPEPSync = async () => {
    setSyncing(prev => ({ ...prev, pep: true }));
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/watchlist/sync/opensanctions-pep`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('OpenSanctions PEP sync started (this may take several minutes)');
        setTimeout(fetchData, 10000);
      } else {
        toast.error('Failed to start PEP sync');
      }
    } catch (error) {
      toast.error('Error starting sync');
    } finally {
      setSyncing(prev => ({ ...prev, pep: false }));
    }
  };

  const triggerWikidataEnrichment = async () => {
    setSyncing(prev => ({ ...prev, wikidata: true }));
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/watchlist/enrich/trigger?batch_size=1000`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setTimeout(fetchData, 5000);
      } else {
        toast.error('Failed to start Wikidata enrichment');
      }
    } catch (error) {
      toast.error('Error starting enrichment');
    } finally {
      setSyncing(prev => ({ ...prev, wikidata: false }));
    }
  };

  const triggerMOHASync = async () => {
    setSyncing(prev => ({ ...prev, moha: true }));
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/watchlist/sync/my-moha`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('MY MOHA sanctions sync started');
        setTimeout(fetchData, 5000);
      } else {
        toast.error('Failed to start MY MOHA sync');
      }
    } catch (error) {
      toast.error('Error starting MY MOHA sync');
    } finally {
      setSyncing(prev => ({ ...prev, moha: false }));
    }
  };

  const triggerUNSCRSync = async () => {
    setSyncing(prev => ({ ...prev, unscr: true }));
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/watchlist/sync/unscr`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('UNSCR sanctions sync started');
        setTimeout(fetchData, 5000);
      } else {
        toast.error('Failed to start UNSCR sync');
      }
    } catch (error) {
      toast.error('Error starting UNSCR sync');
    } finally {
      setSyncing(prev => ({ ...prev, unscr: false }));
    }
  };

  const triggerEUSync = async () => {
    setSyncing(prev => ({ ...prev, eu: true }));
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/watchlist/sync/eu-fsf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('EU Consolidated sanctions sync started');
        setTimeout(fetchData, 5000);
      } else {
        toast.error('Failed to start EU sync');
      }
    } catch (error) {
      toast.error('Error starting EU sync');
    } finally {
      setSyncing(prev => ({ ...prev, eu: false }));
    }
  };

  const handleMOHAUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Please upload a .json file (entities.ftm.json)');
      return;
    }

    setMohaUploading(true);
    const token = localStorage.getItem('token');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/watchlist/upload/my-moha`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.entries_imported} MY MOHA entities imported`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Error uploading file');
    } finally {
      setMohaUploading(false);
      e.target.value = '';
    }
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} activePage="data">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} activePage="data">
      <div className="max-w-7xl mx-auto space-y-8" data-testid="data-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Data Management</h1>
            <p className="text-slate-500 mt-1">Manage watchlist data sources and enrichment</p>
          </div>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {/* List Update Status – Prominent hero row */}
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">List Update Status</h2>
            <p className="text-xs text-slate-500">Live freshness of each sanctions data source</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 pt-3" data-testid="list-update-status-grid">
            <StatusCard
              freshness={freshness}
              source="OFAC SDN"
              label="OFAC SDN"
              count={watchlistCounts?.ofac}
              Icon={Server}
              iconBg="bg-red-50"
              iconColor="text-red-600"
              testId="ofac"
            />
            <StatusCard
              freshness={freshness}
              source="MY MOHA Sanctions"
              label="MY MOHA"
              count={watchlistCounts?.my_moha}
              Icon={Shield}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              testId="moha"
            />
            <StatusCard
              freshness={freshness}
              source="UNSCR"
              label="UNSCR"
              count={watchlistCounts?.unscr}
              Icon={Globe}
              iconBg="bg-sky-50"
              iconColor="text-sky-600"
              testId="unscr"
            />
            <StatusCard
              freshness={freshness}
              source="EU Consolidated"
              label="EU Consolidated"
              count={watchlistCounts?.eu_consolidated}
              Icon={Shield}
              iconBg="bg-indigo-50"
              iconColor="text-indigo-600"
              testId="eu"
            />
            <StatusCard
              freshness={freshness}
              source="OpenSanctions PEP"
              label="OpenSanctions PEP"
              count={watchlistCounts?.opensanctions_pep}
              Icon={Users}
              iconBg="bg-blue-50"
              iconColor="text-blue-600"
              testId="pep"
            />
          </div>
        </div>

        {/* Aggregate counts (secondary) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">OpenSanctions</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="opensanctions-count">
                    {formatNumber(watchlistCounts?.opensanctions)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Globe className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Entries</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="total-count">
                    {formatNumber(watchlistCounts?.total)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Database className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Wikidata Enrichment Status */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-500" />
                  Wikidata Enrichment
                </CardTitle>
                <CardDescription>
                  Enriches PEP data with detailed information from Wikidata
                </CardDescription>
              </div>
              <Badge variant={enrichmentStatus?.enrichment_enabled ? 'default' : 'secondary'}>
                {enrichmentStatus?.enrichment_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Enrichment Progress</span>
                <span className="font-medium text-slate-900">
                  {enrichmentStatus?.progress_percent?.toFixed(1)}%
                </span>
              </div>
              <Progress value={enrichmentStatus?.progress_percent || 0} className="h-3" />
              <div className="flex justify-between text-xs text-slate-500">
                <span>{formatNumber(enrichmentStatus?.enriched_entries)} enriched</span>
                <span>{formatNumber(enrichmentStatus?.remaining_entries)} remaining</span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total PEP Entries</p>
                <p className="text-lg font-semibold text-slate-900">
                  {formatNumber(enrichmentStatus?.total_pep_entries)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">With Wikidata ID</p>
                <p className="text-lg font-semibold text-slate-900">
                  {formatNumber(enrichmentStatus?.entries_with_wikidata_id)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Batch Size</p>
                <p className="text-lg font-semibold text-slate-900">
                  {formatNumber(enrichmentStatus?.batch_size)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Last Enrichment</p>
                <p className="text-sm font-medium text-slate-900">
                  {enrichmentStatus?.last_enrichment?.timestamp 
                    ? formatDate(enrichmentStatus.last_enrichment.timestamp)
                    : 'Never'}
                </p>
              </div>
            </div>

            {/* Trigger Button - Super Admin Only */}
            {isSuperAdmin && (
            <div className="pt-4">
              <Button 
                onClick={triggerWikidataEnrichment} 
                disabled={syncing.wikidata}
                data-testid="trigger-wikidata-btn"
              >
                {syncing.wikidata ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {syncing.wikidata ? 'Starting...' : 'Trigger Manual Enrichment (1000 entries)'}
              </Button>
            </div>
            )}
          </CardContent>
        </Card>

        {/* Sync Controls - Super Admin Only */}
        {isSuperAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* OFAC Sync */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5 text-red-500" />
                OFAC SDN Sync
              </CardTitle>
              <CardDescription>
                Official U.S. Treasury sanctions list (SDN_ENHANCED.XML)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4" />
                <span>Auto-sync: {schedulerStatus?.schedule || 'daily'} at {schedulerStatus?.sync_time || '02:00'}</span>
              </div>
              <Button 
                onClick={triggerOFACSync} 
                disabled={syncing.ofac}
                variant="outline"
                className="w-full"
                data-testid="trigger-ofac-btn"
              >
                {syncing.ofac ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {syncing.ofac ? 'Syncing...' : 'Sync OFAC Now'}
              </Button>
            </CardContent>
          </Card>

          {/* MY MOHA Sync */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                MY MOHA Sanctions
              </CardTitle>
              <CardDescription>
                Malaysia Ministry of Home Affairs sanctions list
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4" />
                <span>Auto-sync: Daily with OFAC</span>
              </div>
              <Button 
                onClick={triggerMOHASync} 
                disabled={syncing.moha}
                variant="outline"
                className="w-full"
                data-testid="trigger-moha-btn"
              >
                {syncing.moha ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {syncing.moha ? 'Syncing...' : 'Sync MY MOHA Now'}
              </Button>
              <div className="pt-2 border-t">
                <label className="w-full cursor-pointer">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleMOHAUpload}
                    className="hidden"
                    data-testid="moha-upload-input"
                  />
                  <div className={`flex items-center justify-center gap-2 w-full px-4 py-2 border border-dashed rounded-md text-sm transition-colors ${mohaUploading ? 'border-slate-300 text-slate-400' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}>
                    {mohaUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {mohaUploading ? 'Uploading...' : 'Upload entities.ftm.json'}
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* UNSCR Sync */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-sky-500" />
                UNSCR Sanctions
              </CardTitle>
              <CardDescription>
                UN Security Council Consolidated Sanctions List
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4" />
                <span>Auto-sync: {schedulerStatus?.unscr_enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              <Button 
                onClick={triggerUNSCRSync} 
                disabled={syncing.unscr}
                variant="outline"
                className="w-full"
                data-testid="trigger-unscr-btn"
              >
                {syncing.unscr ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {syncing.unscr ? 'Syncing...' : 'Sync UNSCR Now'}
              </Button>
              <p className="text-xs text-slate-500">
                Covers UNSCR 1267, 1989, 2253, and other UN sanctions committees.
              </p>
            </CardContent>
          </Card>

          {/* EU Consolidated Sync */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-500" />
                EU Consolidated
              </CardTitle>
              <CardDescription>
                EU Financial Sanctions Files (FSF) — DG FISMA
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4" />
                <span>Auto-sync: {schedulerStatus?.eu_fsf_enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              <Button
                onClick={triggerEUSync}
                disabled={syncing.eu}
                variant="outline"
                className="w-full"
                data-testid="trigger-eu-btn"
              >
                {syncing.eu ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {syncing.eu ? 'Syncing...' : 'Sync EU Now'}
              </Button>
              <p className="text-xs text-slate-500">
                Persons, groups and entities subject to EU financial sanctions and travel bans.
              </p>
            </CardContent>
          </Card>

          {/* PEP Sync */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                OpenSanctions PEP Sync
              </CardTitle>
              <CardDescription>
                Politically Exposed Persons dataset (~700k entries, ~1.2GB)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4" />
                <span>Auto-sync: {schedulerStatus?.opensanctions_pep_enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              <Button 
                onClick={triggerPEPSync} 
                disabled={syncing.pep}
                variant="outline"
                className="w-full"
                data-testid="trigger-pep-btn"
              >
                {syncing.pep ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {syncing.pep ? 'Syncing...' : 'Sync PEP Now (takes ~3 min)'}
              </Button>
              <p className="text-xs text-slate-500">
                After sync completes, Wikidata enrichment will automatically run.
              </p>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Non-admin notice */}
        {!isSuperAdmin && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <p className="text-sm text-amber-800">
                Sync, upload, and enrichment actions are restricted to Super Admin users only. Contact your administrator for data updates.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Recent Sync Logs */}
        <SyncLogsList syncLogs={syncLogs} formatDate={formatDate} formatNumber={formatNumber} />
      </div>
    </Layout>
  );
}
